import { LocalDate } from "@js-joda/core";
import { DatePickerPopup } from "components/designV2/datePicker/datePickerPopup";
import { DatePickerMenuPosition } from "components/designV2/datePicker/datePicker.types";
import {
  buildCalendarDays,
  getStockholmLocalDateNow,
  getCalendarLabels,
  toDayKey,
} from "components/designV2/datePicker/datePicker.utils";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface PeriodDayPickerProps {
  currentDate: LocalDate;
  label: string;
  onChange: (nextDate: LocalDate) => void;
  openAriaLabel: string;
}

const PERIOD_PICKER_Z_INDEX = 2000;
const PERIOD_PICKER_MIN_WIDTH_PX = 320;

export const PeriodDayPicker = ({
  currentDate,
  label,
  onChange,
  openAriaLabel,
}: PeriodDayPickerProps): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<LocalDate>(
    currentDate.withDayOfMonth(1),
  );
  const [position, setPosition] = useState<DatePickerMenuPosition | null>(null);
  const [todayKey, setTodayKey] = useState<string>(() =>
    toDayKey(getStockholmLocalDateNow()),
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const closePicker = useCallback((): void => {
    setIsOpen(false);
    setPosition(null);
  }, []);

  const calendarLabels = useMemo(() => getCalendarLabels(t), [t]);
  const days = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);
  const valueKey = useMemo(() => toDayKey(currentDate), [currentDate]);

  useOnClickOutside([triggerRef, popupRef], closePicker, {
    enabled: isOpen,
    eventType: "mousedown",
  });

  useEffect(() => {
    setCurrentMonth(currentDate.withDayOfMonth(1));
  }, [currentDate]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const nextTodayKey = toDayKey(getStockholmLocalDateNow());
      setTodayKey((currentTodayKey) =>
        currentTodayKey === nextTodayKey ? currentTodayKey : nextTodayKey,
      );
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = (): void => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const popupWidth = Math.max(rect.width, PERIOD_PICKER_MIN_WIDTH_PX);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popupHeight = popupRef.current?.offsetHeight ?? 0;
      const desiredTop = rect.bottom + 12;
      const shouldPlaceBelow = desiredTop + popupHeight <= viewportHeight - 8;
      const top = shouldPlaceBelow
        ? desiredTop
        : Math.max(8, rect.top - popupHeight - 12);
      const left = Math.min(
        Math.max(8, rect.left - (popupWidth - rect.width) / 2),
        Math.max(8, viewportWidth - popupWidth - 8),
      );
      setPosition({ top, left, width: popupWidth });
    };

    updatePosition();
    requestAnimationFrame(updatePosition);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const picker = (
    <DatePickerPopup
      ref={popupRef}
      position={position}
      zIndex={PERIOD_PICKER_Z_INDEX}
      size="compact"
      mode="single"
      showModeButtons={false}
      showHolidayWeekendToggles={false}
      allowRange={false}
      currentMonth={currentMonth}
      monthNames={calendarLabels.monthNames}
      dayHeaders={calendarLabels.dayHeaders}
      days={days}
      todayKey={todayKey}
      valueKey={valueKey}
      notSelectableLabel={t("DatePicker.NotSelectable")}
      onPrevMonth={() => setCurrentMonth((month) => month.minusMonths(1))}
      onNextMonth={() => setCurrentMonth((month) => month.plusMonths(1))}
      onSelectDay={(date) => {
        onChange(date);
        closePicker();
      }}
      isAllowedByMin={() => true}
      matchesMonthlyDay={() => true}
      shouldExclude={() => false}
      isPrevDisabled={false}
      isNextDisabled={false}
    />
  );

  return (
    <>
      <DayLabelButton
        ref={triggerRef}
        type="button"
        onClick={() => {
          setPosition(null);
          setIsOpen(true);
        }}
        aria-label={openAriaLabel}
      >
        {label}
      </DayLabelButton>
      {isOpen && position && typeof document !== "undefined"
        ? createPortal(picker, document.body)
        : null}
    </>
  );
};

const DayLabelButton = styled.button`
  ${TypographyV2.h3};
  color: ${ColorV2.Blue1};
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
